/**
 * Keeps every theme-level cart entry point inside Yagcho's cart drawer.
 *
 * Dawn product-form instances already submit with Ajax. A few legacy/custom
 * buy boxes are plain product forms, so the browser otherwise follows
 * /cart/add and Shopify redirects to the full cart page. This guard upgrades
 * only those plain forms once the configured cart drawer is available.
 */
(() => {
  const normalisePath = (url) => url.pathname.replace(/\/+$/, '') || '/';

  function cartRoute(name) {
    return new URL(window.routes?.[name] || (name === 'cart_add_url' ? '/cart/add' : '/cart'), window.location.origin);
  }

  function getDrawer() {
    const drawer = document.querySelector('cart-drawer');
    return drawer && typeof drawer.renderContents === 'function' && typeof drawer.open === 'function' ? drawer : null;
  }

  function isRoute(url, routeName) {
    const route = cartRoute(routeName);
    return url.origin === route.origin && normalisePath(url) === normalisePath(route);
  }

  async function addToDrawer(formData, { trigger, source = 'yagcho-cart-drawer-guard' } = {}) {
    const drawer = getDrawer();
    if (!drawer) throw new Error('Cart drawer is not available');

    formData.set(
      'sections',
      drawer.getSectionsToRender().map((section) => section.id).join(',')
    );
    formData.set('sections_url', window.location.pathname);
    drawer.setActiveElement(trigger || document.activeElement);

    const response = await fetch(cartRoute('cart_add_url'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });
    const cartData = await response.json();

    if (!response.ok || cartData.status) {
      throw new Error(cartData.description || cartData.message || 'Cart add failed');
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      await publish(PUB_SUB_EVENTS.cartUpdate, {
        source,
        productVariantId: formData.get('id'),
        cartData,
      });
    }

    drawer.classList.remove('is-empty');
    drawer.renderContents(cartData);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartData } }));
    return cartData;
  }

  window.YagchoCartDrawer = Object.assign(window.YagchoCartDrawer || {}, {
    addToDrawer,
    open(trigger) {
      const drawer = getDrawer();
      if (!drawer) return false;
      drawer.open(trigger);
      return true;
    },
  });

  document.addEventListener('submit', async (event) => {
    if (event.defaultPrevented) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.closest('product-form')) return;
    if ((form.method || 'get').toLowerCase() !== 'post' || form.target) return;

    const action = new URL(form.getAttribute('action') || window.location.href, window.location.href);
    if (!isRoute(action, 'cart_add_url') || !getDrawer()) return;

    event.preventDefault();
    const submitter = event.submitter;
    const wasDisabled = submitter?.disabled;
    if (submitter) submitter.disabled = true;

    try {
      await addToDrawer(new FormData(form), { trigger: submitter });
    } catch (error) {
      console.error('[Yagcho] Cart drawer add failed.', error);
      form.dispatchEvent(new CustomEvent('yagcho:cart-add-error', { bubbles: true, detail: { error } }));
    } finally {
      if (submitter) submitter.disabled = wasDisabled;
    }
  });

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('a[href]');
    if (!link || link.target || link.hasAttribute('download')) return;

    const url = new URL(link.href, window.location.href);
    if (!isRoute(url, 'cart_url') || !window.YagchoCartDrawer.open(link)) return;

    event.preventDefault();
  });
})();
